import {
  Button,
  Chip,
  DrawerFooter,
  Link,
} from '@one-thd/sui-atomic-components';
import { DateTime } from 'luxon';
import React from 'react';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { dateTileOptions } from '../../../constants/order-filter';
import { TDateSelectValues } from '../../../models/common';

interface IOrderFilterFooterProps {
  filterSettings: {
    userView: string;
    customDateUsed: boolean;
    showStartCalendar: boolean;
    showEndCalendar: boolean;
    invalidStartDate: boolean;
    invalidEndDate: boolean;
  };
  pHRequestResultsFilter?: string;
  dateSettings: {
    selectedDate: TDateSelectValues | null;
    startDate: DateTime | string | null;
    endDate: DateTime | string | null;
  };
  username: string;
  poJobNameFilter: string;
  paymentFilter: string;
  setFilterSettings: React.Dispatch<
    React.SetStateAction<{
      userView: string;
      customDateUsed: boolean;
      showStartCalendar: boolean;
      showEndCalendar: boolean;
      invalidStartDate: boolean;
      invalidEndDate: boolean;
    }>
  >;
  onDateSelect: (
    _event: React.SyntheticEvent<Element, Event>,
    date: string,
  ) => void;
  setPOJobNameFilter: React.Dispatch<React.SetStateAction<string>>;
  setPaymentFilter: React.Dispatch<React.SetStateAction<string>>;
  onViewResults: () => void;
}

export const OrderFilterFooter = ({
  filterSettings,
  pHRequestResultsFilter,
  dateSettings,
  username,
  poJobNameFilter,
  paymentFilter,
  setFilterSettings,
  onDateSelect,
  setPOJobNameFilter,
  setPaymentFilter,
  onViewResults,
}: IOrderFilterFooterProps) => {
  const defaultDateValue: TDateSelectValues =
    FeatureSwitches.enableLSSCPS25MonthsDefault
      ? 'last25Months'
      : 'last6Months';

  const shouldShowDateChip = dateSettings.selectedDate !== defaultDateValue;
  const shouldShowUserChip = filterSettings.userView !== 'allOrders';
  const activeChipCount = [
    shouldShowDateChip,
    shouldShowUserChip,
    Boolean(poJobNameFilter),
    Boolean(paymentFilter),
  ].filter(Boolean).length;

  const getDateChipLabel = () => {
    if (
      dateSettings.selectedDate === 'custom' &&
      dateSettings.startDate &&
      dateSettings.endDate
    ) {
      return `${dateSettings.startDate.toString()} - ${dateSettings.endDate.toString()}`;
    }

    const selectedDateOption = dateTileOptions.find(
      (option) => option.value === dateSettings.selectedDate,
    );

    if (selectedDateOption?.label) {
      return selectedDateOption.label;
    }

    return (
      dateTileOptions.find((option) => option.value === defaultDateValue)
        ?.label ?? ''
    );
  };

  return (
    <DrawerFooter>
      <div className="sui-grid sui-grid-cols-1 sui-gap-4 sui-w-full sui-mb-2">
        <ul
          className="sui-flex sui-gap-4 sui-max-w-[640px] sui-overflow-x-auto sui-list-none sui-p-0 sui-m-0"
          aria-label="Click any of the filters to remove it"
        >
          {activeChipCount >= 2 && (
            <li>
              <Link
                component="button"
                className="sui-whitespace-nowrap sui-shrink-0"
                onClick={() => {
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    userView: 'allOrders',
                  }));
                  onDateSelect(
                    {} as React.SyntheticEvent<Element, Event>,
                    defaultDateValue,
                  );
                  setPOJobNameFilter('');
                  setPaymentFilter('');
                }}
              >
                Clear All
              </Link>
            </li>
          )}
          {shouldShowDateChip && (
            <li>
              <Chip
                title="Date"
                label={getDateChipLabel()}
                onClick={() =>
                  onDateSelect(
                    {} as React.SyntheticEvent<Element, Event>,
                    defaultDateValue,
                  )
                }
              />
            </li>
          )}

          {shouldShowUserChip && (
            <li>
              <Chip
                title="User"
                label={
                  filterSettings.userView === 'allOrders'
                    ? 'Everyone'
                    : username
                }
                onClick={() =>
                  setFilterSettings((prevState) => ({
                    ...prevState,
                    userView: 'allOrders',
                  }))
                }
              />
            </li>
          )}

          {poJobNameFilter && (
            <li>
              <Chip
                title="PO/Job"
                label={poJobNameFilter}
                onClick={() => setPOJobNameFilter('')}
              />
            </li>
          )}
          {paymentFilter && (
            <li>
              <Chip
                title="Payment"
                label={paymentFilter}
                onClick={() => setPaymentFilter('')}
              />
            </li>
          )}
        </ul>
      </div>
      <Button
        variant="primary"
        fullWidth
        disabled={
          filterSettings.userView === pHRequestResultsFilter &&
          (!dateSettings.startDate || !dateSettings.endDate)
        }
        onClick={onViewResults}
      >
        View Results
      </Button>
    </DrawerFooter>
  );
};
